/**
 * Wishlist Component - Cart Drawer Integration
 * Opens cart drawer when adding products from wishlist page
 */

(function() {
  'use strict';

  // Function to wait for Swym container to appear using MutationObserver
  function waitForWishlistPage(callback) {
    const checkContainer = () => {
      const container = document.querySelector('#swym-storefront-layout-container, [id*="swym"][id*="layout"], [class*="swym"][class*="layout"]');
      if (container) {
        callback();
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkContainer()) {
      return;
    }

    // Wait for container to appear using MutationObserver
    
    const observer = new MutationObserver(function(mutations, obs) {
      if (checkContainer()) {
        obs.disconnect(); // Stop observing once container is found
      }
    });

    // Start observing the document body for changes
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      // If body doesn't exist yet, wait for DOMContentLoaded
      document.addEventListener('DOMContentLoaded', function() {
        if (document.body) {
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        }
      });
    }

    // Fallback timeout - disconnect observer after 30 seconds
    setTimeout(() => {
      observer.disconnect();
      checkContainer();
    }, 30000);
  }

  let cartDrawer = null;
  let lastCartItemCount = null;

  // Note: We use the global sectionsToRender from cart.js via cartDrawer.getSectionsToRender()
  // No need to maintain our own local copy

  // Fetch cart sections and update cart drawer
  function updateCartDrawer() {
    // Ensure cart drawer exists
    if (!cartDrawer) {
      cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.error('Cart drawer element not found');
        return;
      }
    }

    // Use the cart drawer's getSectionsToRender method to get the correct sections
    // This uses the global sectionsToRender from cart.js
    let sectionsToUse = [];
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sectionsToUse = cartDrawer.getSectionsToRender();
    } else {
      console.error('cartDrawer.getSectionsToRender is not available');
      // Try to open drawer anyway
      cartDrawer.open();
      return;
    }

    if (!sectionsToUse || sectionsToUse.length === 0) {
      console.error('No sections to render');
      // Try to open drawer anyway
      cartDrawer.open();
      return;
    }

    const sectionIds = sectionsToUse.map(s => s.section).join(',');
    const sectionsUrl = window.location.pathname;
    const url = `${window.routes.cart_url}?sections=${sectionIds}&sections_url=${sectionsUrl}`;
    
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // Shopify may return sections directly or nested under 'sections'
        // Check if sections are directly in response or nested
        let sectionsData = data;
        if (data.sections) {
          // Already nested under sections
          sectionsData = data;
        } else {
          // Sections are direct keys - wrap them in a sections object
          sectionsData = { sections: data };
        }
        
        // Ensure sections property exists
        if (!sectionsData.sections) {
          console.error('Response does not have sections property:', sectionsData);
          // Try to open drawer anyway with current cart
          cartDrawer.open();
          return;
        }

        // Update cart drawer with new sections (this also opens the drawer)
        try {
          // Verify sections exist in response
          sectionsToUse.forEach(section => {
            if (!sectionsData.sections[section.section]) {
              console.warn('Section not found in response:', section.section);
            }
          });
          
          cartDrawer.renderContents(sectionsData, true);
          
          // Also update cart counters manually to ensure they're updated
          if (typeof updateCartCounters === 'function') {
            updateCartCounters();
          }
          
          // Force a re-check of cart items in drawer after a moment
          setTimeout(() => {
            const cartItems = cartDrawer.querySelectorAll('.cart-item, cart-item');
            const firstSection = cartDrawer.getSectionsToRender()?.[0]?.section || '';
            const cartData = sectionsData.sections[firstSection];
            
            // If cart items don't match, try to re-render
            if (cartData) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(cartData, 'text/html');
              const itemsInSection = doc.querySelectorAll('.cart-item, cart-item').length;
              
              if (cartItems.length !== itemsInSection) {
                cartDrawer.renderContents(sectionsData, false); // Don't open again, just update
              }
            }
          }, 500);
        } catch (error) {
          console.error('Error rendering cart contents:', error, error.stack);
          // Fallback: just open the drawer
          cartDrawer.open();
        }
      })
      .catch(error => {
        console.error('Error updating cart drawer:', error);
        // Fallback: try to open drawer anyway
        cartDrawer.open();
      });
  }

  // Check if cart has been updated
  function checkCartUpdate() {
    return fetch(`${window.routes.cart_url}.js`)
      .then(response => response.json())
      .then(cartData => {
        const currentItemCount = cartData.item_count;
        
        // If cart item count increased, update cart drawer
        if (lastCartItemCount !== null && currentItemCount > lastCartItemCount) {
          updateCartDrawer();
          return true; // Cart was updated
        }
        
        lastCartItemCount = currentItemCount;
        return false; // Cart was not updated
      })
      .catch(error => {
        // Silently fail if cart API is unavailable
        return false;
      });
  }

  // Monitor cart updates using MutationObserver on cart count elements
  function monitorCartAfterClick() {
    // Watch for changes to cart count elements in the DOM
    const cartCounter = document.querySelector('.cart-drawer__title-counter, #cart-counter, [data-cart-count]');
    
    if (cartCounter) {
      const initialText = cartCounter.textContent || cartCounter.innerText;
      
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const currentText = cartCounter.textContent || cartCounter.innerText;
            if (currentText !== initialText) {
              observer.disconnect();
              updateCartDrawer();
            }
          }
        });
      });

      observer.observe(cartCounter, {
        childList: true,
        subtree: true,
        characterData: true
      });

      // Also check periodically via API as fallback (but less frequently)
      const apiCheck = setTimeout(async () => {
        const wasUpdated = await checkCartUpdate();
        if (wasUpdated) {
          observer.disconnect();
        }
      }, 1000);
      
      setTimeout(() => {
        observer.disconnect();
        clearTimeout(apiCheck);
      }, 5000);
    }
  }

  // Get product data from Swym item
  async function getProductDataFromSwymItem(button) {
    // Try multiple methods to find variant ID or product handle
    // Method 1: Check data attributes on button
    const variantId = button.dataset.variantId || 
                      button.dataset.variant || 
                      button.getAttribute('data-variant-id') ||
                      button.getAttribute('data-variant');
    
    if (variantId) {
      return { variantId: variantId };
    }

    // Method 2: Look for product handle or ID in container
    const itemContainer = button.closest('.swym-storefront-layout-grid-item, [data-product-id], [data-product-handle]');
    if (itemContainer) {
      // Try to find variant selector in the container
      const variantInput = itemContainer.querySelector('input[name="id"], [data-variant-id], [data-variant]');
      if (variantInput) {
        const vid = variantInput.value || variantInput.dataset.variantId || variantInput.dataset.variant || 
                   variantInput.getAttribute('data-variant-id') || variantInput.getAttribute('data-variant');
        if (vid) return { variantId: vid };
      }

      // Get product handle or ID
      const productHandle = itemContainer.dataset.productHandle || itemContainer.getAttribute('data-product-handle');
      const productId = itemContainer.dataset.productId || itemContainer.getAttribute('data-product-id');
      
      if (productHandle) {
        // Fetch product data to get first available variant
        try {
          const response = await fetch(`/products/${productHandle}.js`);
          const product = await response.json();
          if (product.variants && product.variants.length > 0) {
            const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
            return { variantId: firstAvailable.id.toString() };
          }
        } catch (error) {
          console.error('Error fetching product:', error);
        }
      }
      
      if (productId) {
        // If we have product ID, try to get handle from URL or fetch
        const productLink = itemContainer.querySelector('a[href*="/products/"]');
        if (productLink) {
          const href = productLink.getAttribute('href');
          const match = href.match(/\/products\/([^/?]+)/);
          if (match) {
            try {
              const response = await fetch(`/products/${match[1]}.js`);
              const product = await response.json();
              if (product.variants && product.variants.length > 0) {
                const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
                return { variantId: firstAvailable.id.toString() };
              }
            } catch (error) {
              console.error('Error fetching product:', error);
            }
          }
        }
      }
    }

    // Method 3: Look for product link in the item
    const productLink = button.closest('[data-product-id]')?.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      const match = href.match(/\/products\/([^/?]+)/);
      if (match) {
        try {
          const response = await fetch(`/products/${match[1]}.js`);
          const product = await response.json();
          if (product.variants && product.variants.length > 0) {
            const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
            return { variantId: firstAvailable.id.toString() };
          }
        } catch (error) {
          console.error('Error fetching product:', error);
        }
      }
    }

    return null;
  }

  // Handle add to cart properly with correct format
  function handleSwymAddToCart(button, variantId) {
    if (!variantId) {
      console.error('Variant ID not found for Swym add to cart');
      return;
    }

    if (!cartDrawer) {
      cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.error('Cart drawer not found');
        return;
      }
    }

    // Get sections from cart drawer
    const sectionsToUse = cartDrawer.getSectionsToRender ? cartDrawer.getSectionsToRender() : [];
    if (!sectionsToUse || sectionsToUse.length === 0) {
      console.error('No sections to render');
      return;
    }

    // Create FormData with proper format
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sectionsToUse.map(s => s.section).join(','));
    formData.append('sections_url', window.location.pathname);

    // Use fetchConfig similar to theme's implementation
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    };

    // Add loading state to button
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    fetch(`${window.routes.cart_add_url}`, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          // Error occurred
          console.error('Add to cart error:', response.message || response.description);
          alert(response.message || response.description || 'Unable to add product to cart');
          button.disabled = false;
          button.textContent = originalText;
          return;
        }

        // Success - update cart drawer
        cartDrawer.renderContents(response, true);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        alert('Unable to add product to cart. Please try again.');
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  // Swym callback function - executed after Swym loads
  function onSwymLoadCallback(swat) {
    if (!swat) {
      console.error('Swym swat object is null or undefined');
      return;
    }

    if (!swat.evtLayer || !swat.JSEvents) {
      console.error('Swym swat object is not properly initialized', swat);
      return;
    }

    // Listen for Swym's UIAddedToCart event
    try {
      const eventName = swat.JSEvents.UIAddedToCart;
      
      if (!eventName) {
        console.error('UIAddedToCart event not found in JSEvents');
        // Try common alternative names
        const alternatives = ['addedToCart', 'itemAddedToCart', 'cartItemAdded', 'productAddedToCart'];
        alternatives.forEach(alt => {
          if (swat.JSEvents[alt]) {
            swat.evtLayer.addEventListener(swat.JSEvents[alt], function(event) {
              handleCartUpdate();
            });
          }
        });
        return;
      }
      
      swat.evtLayer.addEventListener(eventName, function(event) {
        handleCartUpdate();
      });
    } catch (error) {
      console.error('Error adding Swym event listener:', error);
    }

    // Also listen for other Swym events that might indicate cart updates
    if (swat.JSEvents.cartUpdated) {
      swat.evtLayer.addEventListener(swat.JSEvents.cartUpdated, function(event) {
        handleCartUpdate();
      });
    }
  }

  // Helper function to handle cart updates
  function handleCartUpdate() {
    // Get cart drawer directly
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      console.error('Cart drawer element not found');
      return;
    }
    
    // Wait longer for cart to update on server (Swym needs time to process)
    setTimeout(() => {
      updateCartDrawer();
      
      // Double-check and retry if needed
      setTimeout(() => {
        // Verify cart was updated by checking cart count
        fetch(`${window.routes.cart_url}.js`)
          .then(response => response.json())
          .then(cartData => {
            const drawerElement = document.querySelector('cart-drawer');
            const cartBody = drawerElement?.querySelector('#CartDrawer-Body');
            
            // If cart drawer is open but doesn't show the new item, update again
            if (drawerElement && drawerElement.classList.contains('is-visible')) {
              const itemsInDrawer = cartBody?.querySelectorAll('.cart-item, cart-item').length || 0;
              if (itemsInDrawer !== cartData.item_count) {
                updateCartDrawer();
              }
            }
          })
          .catch(() => {});
      }, 1000);
    }, 800);
  }

  // Setup Swym callbacks
  function setupSwymCallbacks() {
    // Initialize SwymCallbacks array if it doesn't exist
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    // Check if we've already added our callback
    const hasCallback = window.SwymCallbacks.some(cb => cb === onSwymLoadCallback);
    
    if (!hasCallback) {
      // Add our callback to Swym's callback array
      window.SwymCallbacks.push(onSwymLoadCallback);
    }
    
    // Also check if Swym is already loaded
    if (window.swat) {
      onSwymLoadCallback(window.swat);
    }
  }

  // Initialize Swym cart integration
  function initSwymCartIntegration() {
    // Setup Swym callbacks first
    setupSwymCallbacks();

    // Listen for clicks on Swym add to cart buttons
    const wishlistContainer = document.querySelector('#swym-storefront-layout-container');
    if (wishlistContainer) {
      // Use event delegation to catch add to cart button clicks
      wishlistContainer.addEventListener('click', async function(event) {
        // Check if clicked element or parent is an add to cart button
        const addToCartButton = event.target.closest('.swym-storefront-layout-grid-item-add-to-cart-button, [data-swym-add-to-cart], button[class*="add-to-cart"]');
        
        if (addToCartButton) {
          // Store initial cart count
          const initialCheck = await fetch(`${window.routes.cart_url}.js`).then(r => r.json()).catch(() => ({ item_count: 0 }));
          const initialItemCount = initialCheck.item_count || 0;
          lastCartItemCount = initialItemCount;

          // Try to intercept and handle ourselves first
          const productData = await getProductDataFromSwymItem(addToCartButton);
          
          if (productData && productData.variantId) {
            // Handle add to cart with proper format ourselves
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleSwymAddToCart(addToCartButton, productData.variantId);
          } else {
            // If we can't intercept, let Swym handle it but monitor for cart changes
            // Monitor cart aggressively after Swym processes
            monitorCartAfterClick();
            
            // Also check for notification container appearing
            setTimeout(() => {
              const notificationContainer = document.querySelector('.swym-storefront-layout-notification-container, [class*="swym"][class*="notification"]');
              if (notificationContainer) {
                const display = window.getComputedStyle(notificationContainer).display;
                const visibility = window.getComputedStyle(notificationContainer).visibility;
                const opacity = window.getComputedStyle(notificationContainer).opacity;
                
                if (display !== 'none' && visibility !== 'hidden' && parseFloat(opacity) > 0 && notificationContainer.children.length > 0) {
                  setTimeout(() => updateCartDrawer(), 300);
                }
              }
            }, 500);
          }
        }
      }, true); // Use capture phase to catch early
    }

    // Method 2: Watch for Swym notification container changes
    function watchSwymNotificationContainer() {
      // Find or wait for notification container
      const findNotificationContainer = () => {
        return document.querySelector('.swym-storefront-layout-notification-container, [class*="swym"][class*="notification"]');
      };

      let notificationContainer = findNotificationContainer();
      
      // If not found, wait for it to appear using MutationObserver
      if (!notificationContainer) {
        const containerObserver = new MutationObserver(function(mutations, obs) {
          notificationContainer = findNotificationContainer();
          if (notificationContainer) {
            obs.disconnect();
            setupNotificationWatcher(notificationContainer);
          }
        });

        if (document.body) {
          containerObserver.observe(document.body, {
            childList: true,
            subtree: true
          });
        }

        // Stop observing after 5 seconds
        setTimeout(() => containerObserver.disconnect(), 5000);
      } else {
        setupNotificationWatcher(notificationContainer);
      }

      function setupNotificationWatcher(container) {
        // Watch for visibility changes or content changes
        const observer = new MutationObserver(function(mutations) {
          let shouldUpdate = false;
          
          mutations.forEach(function(mutation) {
            // Check if container became visible or content was added
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              const display = window.getComputedStyle(container).display;
              const visibility = window.getComputedStyle(container).visibility;
              const opacity = window.getComputedStyle(container).opacity;
              
              // If container is now visible
              if (display !== 'none' && visibility !== 'hidden' && parseFloat(opacity) > 0) {
                shouldUpdate = true;
              }
            }
            
            // Check if child nodes were added (notification content appeared)
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              shouldUpdate = true;
            }
            
            // Check for class changes that indicate visibility
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const classes = container.className;
              // Check if classes suggest it's visible (no 'hidden', has 'show', 'visible', etc.)
              if (classes && !classes.includes('hidden') && 
                  (classes.includes('show') || classes.includes('visible') || 
                   container.offsetHeight > 0 || container.offsetWidth > 0)) {
                shouldUpdate = true;
              }
            }
          });

          if (shouldUpdate) {
            // Notification container was updated/shown, cart was added
            setTimeout(() => updateCartDrawer(), 300);
          }
        });

        // Observe the notification container
        observer.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'data-visible']
        });

        // Observe visibility changes via style and class mutations
        // The observer already watches for style and class changes, so no interval needed
      }
    }

    // Start watching for notification container
    watchSwymNotificationContainer();

    // Method 3: Initialize cart item count
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(() => {
        fetch(`${window.routes.cart_url}.js`)
          .then(response => response.json())
          .then(cartData => {
            lastCartItemCount = cartData.item_count;
          })
          .catch(() => {});
      }, 1000);
    }

    // Method 4: Listen for custom events and Swym events
    document.addEventListener('swym:add-to-cart', function() {
      setTimeout(() => updateCartDrawer(), 500);
    });

    document.addEventListener('cart:updated', function() {
      updateCartDrawer();
    });

    // Listen for any Swym-related events
    if (window.SwymEventData || window.swym) {
      document.addEventListener('swym:cart:add', function() {
        setTimeout(() => updateCartDrawer(), 500);
      });
    }

    // Method 5: Intercept fetch requests to cart/add.js to detect when Swym adds to cart
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      if (typeof url === 'string' && url.includes('/cart/add.js')) {
        return originalFetch.apply(this, args)
          .then(response => {
            // If it's a successful add to cart (200 status)
            if (response.status === 200) {
              // Wait a moment for Swym to finish, then update cart drawer
              setTimeout(() => {
                updateCartDrawer();
              }, 300);
            }
            return response;
          });
      }
      return originalFetch.apply(this, args);
    };
  }

  // Simplified approach: Watch for cart changes when button is clicked
  function setupSimpleCartWatcher() {
    const wishlistContainer = document.querySelector('#swym-storefront-layout-container');
    if (!wishlistContainer) return;

    // Track initial cart count
    let initialCartCount = null;
    
    // Initialize cart count
    fetch(`${window.routes.cart_url}.js`)
      .then(response => response.json())
      .then(cartData => {
        initialCartCount = cartData.item_count;
        lastCartItemCount = cartData.item_count;
      })
      .catch(() => {});

    // Watch for add to cart button clicks
    wishlistContainer.addEventListener('click', function(event) {
      const button = event.target.closest('.swym-storefront-layout-grid-item-add-to-cart-button, button[class*="add-to-cart"]');
      
      if (button) {
        // Get current cart count before Swym adds
        fetch(`${window.routes.cart_url}.js`)
          .then(response => response.json())
          .then(cartData => {
            const beforeCount = cartData.item_count;
            
            // Use MutationObserver to watch cart counter instead of polling
            const cartCounter = document.querySelector('.cart-drawer__title-counter, #cart-counter, [data-cart-count]');
            
            if (cartCounter) {
              const initialValue = cartCounter.textContent || cartCounter.innerText;
              
              const cartObserver = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                  const currentValue = cartCounter.textContent || cartCounter.innerText;
                  if (currentValue !== initialValue) {
                    const currentCount = parseInt(currentValue) || 0;
                    if (currentCount > beforeCount) {
                      cartObserver.disconnect();
                      updateCartDrawer();
                    }
                  }
                });
              });

              cartObserver.observe(cartCounter, {
                childList: true,
                subtree: true,
                characterData: true
              });

              // Fallback API check if DOM counter doesn't update
              setTimeout(async () => {
                const wasUpdated = await checkCartUpdate();
                if (wasUpdated) {
                  cartObserver.disconnect();
                }
              }, 1000);
              
              // Stop observing after 5 seconds
              setTimeout(() => cartObserver.disconnect(), 5000);
            } else {
              // Fallback: check API a few times if counter element doesn't exist
              const apiChecks = [500, 1000, 1500, 2000];
              apiChecks.forEach(delay => {
                setTimeout(async () => {
                  const wasUpdated = await checkCartUpdate();
                  if (wasUpdated) {
                    return; // Stop checking
                  }
                }, delay);
              });
            }
          })
          .catch(() => {});
      }
    });
  }

  // Wait for DOM to be ready and Swym to be available
  function initWhenReady() {
    // Setup Swym callbacks (if Swym uses them)
    setupSwymCallbacks();

    // Initialize the rest of the integration
    initSwymCartIntegration();
    
    // Setup simple cart watcher as fallback
    setupSimpleCartWatcher();

    // Check if Swym is already loaded
    // Since Swym callbacks are set up, Swym will call our callback when it loads
    // But we also check once when document is ready
    if (window.swat && window.swat.evtLayer) {
      onSwymLoadCallback(window.swat);
    } else {
      // Wait for document to be fully loaded, then check once
      if (document.readyState === 'complete') {
        // Check once after a short delay
        setTimeout(() => {
          if (window.swat && window.swat.evtLayer) {
            onSwymLoadCallback(window.swat);
          }
        }, 1000);
      } else {
        // Wait for document to be complete
        window.addEventListener('load', function() {
          setTimeout(() => {
            if (window.swat && window.swat.evtLayer) {
              onSwymLoadCallback(window.swat);
            }
          }, 1000);
        }, { once: true });
      }
    }
  }

  // Wait for wishlist page to load before initializing
  waitForWishlistPage(function() {
    // Setup immediately once container is found
    setupSwymCallbacks();
    setupSimpleCartWatcher();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(initWhenReady, 500);
      });
    } else {
      setTimeout(initWhenReady, 500);
    }
  });
})();

